import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { EMPTY, Subject, catchError, combineLatest, expand, forkJoin, map, of, reduce, startWith, switchMap } from 'rxjs';
import { DivineServiceApiService, DivineServiceListDto } from '@core/api/services/divine-service-api.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { AgendaComponent } from '@shared/components/agenda/agenda.component';
import { AgendaMonth, createAgendaMonths, groupAgendaItems } from '@shared/components/agenda/agenda.model';
import { UnitService } from '../services/unit.service';
import { OrganizationLevel } from '../models/unit.model';

@Component({
  selector: 'app-unit-agenda-page',
  standalone: true,
  imports: [CommonModule, RouterLink, MatIconModule, AgendaComponent],
  templateUrl: './unit-agenda-page.component.html',
  styleUrl: './unit-agenda-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitAgendaPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(DivineServiceApiService);
  private readonly units = inject(UnitService);
  private readonly userInfo = inject(UserFullInfoService);
  private readonly reload$ = new Subject<void>();
  readonly vm$ = combineLatest([
    this.route.parent!.parent!.paramMap,
    this.userInfo.getUserInfo$(),
    this.reload$.pipe(startWith(void 0)),
  ]).pipe(switchMap(([params, user]) => {
    const unitId = params.get('unit-id')!;
    const canView = !!(user?.isAdmin || user?.readableUnitIds.some(id => id.toLowerCase() === unitId.toLowerCase()));
    const empty = { unitId, months: [] as AgendaMonth<DivineServiceListDto>[], showCommunity: false, loading: false, error: false, canView };
    if (!canView) return of(empty);
    const today = new Date();
    const range = createAgendaMonths(today);
    const first = range[0];
    const last = range[range.length - 1];
    const start = this.dateParameter(new Date(first.year, first.month, 1));
    const end = this.dateParameter(new Date(last.year, last.month + 1, 0));
    const loadPage = (page: number) => this.api.getDivineServices(unitId, start, end, 'date', 'asc', page, 100);
    const services$ = loadPage(1).pipe(
      expand(page => (page.pageNumber ?? 1) < (page.totalPages ?? 1) ? loadPage((page.pageNumber ?? 1) + 1) : EMPTY),
      reduce((items, page) => [...items, ...(page.items ?? [])], [] as DivineServiceListDto[]),
    );
    return forkJoin({ services: services$, unit: this.units.getUnit(unitId) }).pipe(
      map(({ services, unit }) => ({
        ...empty,
        months: groupAgendaItems(services, service => new Date(service.startAt ?? ''), today),
        showCommunity: unit.organizationLevelId < OrganizationLevel.Comunidad,
      })),
      startWith({ ...empty, loading: true }),
      catchError(() => of({ ...empty, error: true })),
    );
  }));

  retry(): void { this.reload$.next(); }

  private dateParameter(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
